import React, { useState, FunctionComponent } from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { AnimatePresence, motion } from 'framer-motion';

import { MerchProduct } from '@services/merch/types/api/merch-product';

import SimpleArtist from '@common/types/artist/simple-artist';

import Flex from '@components/layout/flex';
import Avatar from '@components/basics/avatar';
import { easeTransition } from '@components/framer';
import Typography from '@components/basics/typography';

import MerchCatalogInfoRow from './info-row';
import CatalogOwnerMerchRow from './owner-merch-row';
import CatalogArtistMerchRow from './artist-merch-row';
import { SimpleRelease } from '@common/types/release/simple-release';

interface MerchRowProps {
  user: any;
  allReleases: SimpleRelease[];
  item: MerchProduct;
  artist: SimpleArtist;
  className?: string;
  isMobile: boolean;
  isArtist?: boolean;
  isFilteredByAddedDate: boolean;
}

const mapStateToProps = (state: AppState) => ({
  allReleases: state.release.summary.all?.list || [],
  user: state.user.profile,
  isMobile: state.ui.mobile,
  isArtist: state.activeProfile.isArtist,
});

const MerchRow: FunctionComponent<MerchRowProps> = ({
  item,
  allReleases,
  user,
  artist,
  className,
  isMobile,
  isArtist,
  isFilteredByAddedDate,
}) => {
  const [listOpen, setListOpen] = useState(false);

  const heightMotion = {
    visible: {
      opacity: 1,
      height: 'auto',
      transition: {
        ...easeTransition,
      },
    },
    hidden: {
      opacity: 0,
      height: 0,
    },
  };

  return (
    <Row className={className || ''}>
      <MerchCatalogInfoRow
        user={user}
        allReleases={allReleases}
        merchProduct={item}
        artist={artist}
        isListOpen={listOpen}
        openList={() => setListOpen(!listOpen)}
        isFilteredByAddedDate={isFilteredByAddedDate}
      />
      <AnimatePresence exitBeforeEnter initial={false}>
        {listOpen && (
          <RowWraper
            initial="hidden"
            animate={listOpen ? 'visible' : 'hidden'}
            exit="hidden"
            variants={heightMotion}
          >
            {!isMobile ? (
              <RowHeader>
                {isArtist ? null : (
                  <Avatar
                    className="avatar"
                    size="24"
                    src={artist.avatar}
                    alt={artist.name}
                  />
                )}
                <Typography className="song-list" weight="700">
                  <Translate
                    id={
                      isArtist
                        ? 'CATALOG.MERCH.HEADER.ASSOCIATED_RELEASE'
                        : 'CATALOG.MERCH.HEADER.QUANTITY'
                    }
                  />
                </Typography>
                <Typography className="third-column" weight="700">
                  {/* <Translate
                    id={
                      isArtist
                        ? 'CATALOG.MERCH.HEADER.QUALITY'
                        : 'CATALOG.MERCH.HEADER.OWNERSHIP'
                    }
                  /> */}
                  {!isArtist ? (
                    <Translate id="CATALOG.MERCH.HEADER.OWNERSHIP" />
                  ) : null}
                </Typography>
                <Typography className="fourth-column" weight="700">
                  <Translate
                    id={
                      isArtist
                        ? 'CATALOG.MERCH.HEADER.QUANTITY_LEFT'
                        : 'CATALOG.MERCH.HEADER.STAKEHOLDERS'
                    }
                  />
                </Typography>
              </RowHeader>
            ) : (
              <Header
                justifyContent="space-between"
                alignItems="center"
                gap={10}
              >
                <Typography weight="700">
                  <Translate
                    id={
                      isArtist
                        ? 'CATALOG.MERCH.HEADER.ASSOCIATED_RELEASE'
                        : 'CATALOG.TRACKS.HEADER.MAIN'
                    }
                  />
                </Typography>
                {artist && !isArtist && (
                  <Avatar size="24" src={artist.avatar} alt={artist.name} />
                )}
              </Header>
            )}
            <RowList>
              {isArtist ? (
                <CatalogArtistMerchRow item={item} />
              ) : (
                <CatalogOwnerMerchRow item={item} />
              )}
            </RowList>
          </RowWraper>
        )}
      </AnimatePresence>
    </Row>
  );
};

export default connect(mapStateToProps)(MerchRow);

const Row = styled.div`
  width: 100%;
  min-height: 96px;
`;

const RowList = styled.div`
  display: grid;
  grid-gap: 20px;
`;

const RowWraper = styled(motion.div)`
  width: 100%;
  padding: 30px;
  background: ${({ theme }) => theme.colors.differentBackground};
  border: 1px solid ${({ theme }) => theme.colors.box};
  border-radius: 0 0 10px 10px;
`;

const RowHeader = styled.div`
  margin-bottom: 20px;

  display: grid;
  grid-gap: 24px;
  grid-template-columns: repeat(18, minmax(0, 1fr));

  .avatar {
    grid-column: 1 / 2;
  }

  .song-list {
    grid-column: 2 / 8;
  }

  .third-column {
    grid-column: 8 / 11;
  }

  .fourth-column {
    grid-column: 11 / 15;
  }
`;

const Header = styled(Flex)`
  margin-bottom: 20px;
`;
